import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faShieldAlt, faUsers, faWindowMinimize } from '@/fontawesome.ts';
import type { DayTradingParticipant } from '@/models/day-trading-participant.ts';
import type { User } from '@/models/user.ts';
import type { RFC3339 } from '@/models/base-types.ts';
import {formatRelativeTime} from "@/utils/helpers.ts";
import Tooltip from "@/components/ui/Tooltip.tsx";

interface ParticipantsListProps {
  participants: DayTradingParticipant[];
  moderators: User[];
  moderatorPings: { [userID: string]: RFC3339 };
  moderatorPolls: { [userID: string]: RFC3339 };
  sessionClosed: boolean;
  onMinimize?: () => void;
}

function isActive(lastPing: RFC3339, lastPoll?: RFC3339): {lastInteraction: RFC3339|undefined, activeColor: string} {

  if (lastPing === undefined && lastPoll === undefined) return {lastInteraction: undefined, activeColor: 'text-gray-500'};

  const pingTime = (lastPing !== undefined) ? new Date(lastPing).getTime() : undefined;
  const pollTime = (lastPoll !== undefined) ? new Date(lastPoll).getTime() : undefined;

  const now = Date.now();

  if (pingTime !== undefined && now - pingTime < 8*1000) {
    return {lastInteraction: lastPing, activeColor: 'text-green-500'};
  } else if (pollTime !== undefined && now - pollTime < 8*1000) {
    return {lastInteraction: lastPoll!, activeColor: 'text-orange-500'};
  } else {
    if (pingTime !== undefined && pollTime !== undefined) return {lastInteraction: (pollTime < pingTime) ? lastPing : lastPoll, activeColor: 'text-red-500'};
    if (pingTime === undefined)                           return {lastInteraction: lastPoll,                                    activeColor: 'text-red-500'};
    if (pollTime === undefined)                           return {lastInteraction: lastPing,                                    activeColor: 'text-red-500'};

    return {lastInteraction: undefined, activeColor: 'text-red-500'};
  }
}

export default function ParticipantsList({ participants, moderators, moderatorPings, moderatorPolls, sessionClosed, onMinimize }: ParticipantsListProps) {
  const moderatorIds = new Set(moderators.map(m => m.id));
  const filteredParticipants = participants.filter(p => !moderatorIds.has(p.userID));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faUsers} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="font-semibold text-gray-900 dark:text-white text-sm">Teilnehmer</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{filteredParticipants.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {onMinimize !== undefined && (
            <button
              onClick={onMinimize}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer transition-colors"
              title="Minimieren"
            >
              <FontAwesomeIcon icon={faWindowMinimize} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700 overflow-y-auto">
        {moderators.map((m) => {
          const ping = moderatorPings[m.id];
          const poll = moderatorPolls[m.id];
          const {lastInteraction, activeColor} = isActive(ping, poll);
          const displayName = `${m.firstName} ${m.lastName}`.trim();

          return (
            <div key={m.id} className="px-4 py-2 flex items-center gap-2 text-sm">
              {sessionClosed ? (
                <FontAwesomeIcon icon={faCircle} className={`w-2 h-2 text-gray-300`}/>
              ) : (
                <Tooltip text={lastInteraction !== undefined ? ('Letzter Ping: ' + formatRelativeTime(lastInteraction)) : ''}>
                  <FontAwesomeIcon icon={faCircle} className={`w-2 h-2 ${activeColor}`}/>
                </Tooltip>
              )}
              <span className="text-gray-900 dark:text-white truncate font-medium">{displayName}</span>
              <FontAwesomeIcon icon={faShieldAlt} className="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0" title="Moderator"/>
            </div>
          );
        })}

        {filteredParticipants.map((p) => {
          const {lastInteraction, activeColor} = isActive(p.lastPing, p.lastPoll);
          const displayName = p.user !== null ? `${p.user.firstName} ${p.user.lastName}`.trim() : p.userID;

          return (
            <div key={p.id} className="px-4 py-2 flex items-center gap-2 text-sm">
              {sessionClosed ? (
                <FontAwesomeIcon icon={faCircle} className={`w-2 h-2 text-gray-300`}/>
              ) : (
                <Tooltip text={lastInteraction !== undefined ? ('Letzter Ping: ' + formatRelativeTime(lastInteraction)) : ''}>
                  <FontAwesomeIcon icon={faCircle} className={`w-2 h-2 ${activeColor}`}/>
                </Tooltip>
              )}
              <span className="text-gray-900 dark:text-white truncate">{displayName}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
